import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Linkedin, ArrowUpRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";

const TEAM_IMG = "/team.jpg";

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export default function About() {
  const [board, setBoard] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/board-members").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-paper min-h-screen" data-testid="about-page">
      <Navbar />

      {/* HERO — dark area, white logo + white text */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <video
          src="/videos/about-clip.mp4"
          poster="/videos/about-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="about-hero-video"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20 w-full">
          <div className="mb-8"><Logo dark className="h-9" /></div>
          <p className="font-sans text-paper/70 text-xs md:text-sm tracking-[0.28em] uppercase mb-5">About us</p>
          <h1 className="font-serif text-paper font-black tracking-tight leading-[0.98] text-5xl md:text-7xl max-w-4xl">
            Soul &amp; Service, in motion.
          </h1>
          <p className="font-sans text-paper/80 text-base md:text-xl max-w-2xl mt-6 leading-relaxed">
            Tiger Trailride began with a simple idea: that line dancing could bring LSU students
            together — and that the energy on the floor could turn into real service for our community.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <p className="font-sans text-xs tracking-[0.24em] uppercase text-trailride font-semibold">Who we are</p>
        </div>
        <div className="md:col-span-8 max-w-2xl">
          <p className="font-serif text-2xl md:text-4xl text-ink leading-snug">
            We move together on the floor and off it — building belonging, giving back to Baton Rouge,
            and helping each other move forward.
          </p>
          <p className="font-sans text-ink/70 text-lg leading-relaxed mt-8">
            Soul means culture, music, and community. Service means volunteering, student success, and
            campus impact. Together they shape everything we do — from line dancing nights to service
            days to professional development.
          </p>
        </div>
      </section>

      {/* EXECUTIVE BOARD */}
      <section className="border-t border-ink/10" data-testid="board-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="mb-14">
            <p className="font-sans text-xs tracking-[0.24em] uppercase text-trailride font-semibold mb-4">Leadership</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-ink tracking-tight">Executive Board</h2>
            <p className="font-sans text-ink/60 mt-4 max-w-xl">The students building culture and creating opportunities at LSU.</p>
          </div>

          {board.length === 0 ? (
            <p className="font-sans text-ink/50">Board members coming soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {board.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
                  data-testid={`board-member-${m.id}`}
                >
                  <div className="aspect-[4/5] bg-ink overflow-hidden mb-5 flex items-center justify-center">
                    {m.image_url ? (
                      <img src={m.image_url} alt={m.name} className="w-full h-full object-cover grayscale-img" />
                    ) : (
                      <span className="font-serif text-paper/30 text-5xl italic">{initials(m.name)}</span>
                    )}
                  </div>
                  <h3 className="font-serif text-xl font-bold text-ink leading-tight">{m.name}</h3>
                  <p className="font-sans text-sm text-trailride font-medium mt-1">{m.title}</p>
                  {m.bio && <p className="font-sans text-sm text-ink/65 leading-relaxed mt-3">{m.bio}</p>}
                  {m.linkedin_url && (
                    <a
                      href={m.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`board-linkedin-${m.id}`}
                      className="inline-flex items-center gap-1.5 font-sans text-sm text-ink/60 hover:text-trailride mt-3 transition-colors"
                    >
                      <Linkedin size={15} /> LinkedIn
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-ink text-paper" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="mb-14">
            <p className="font-sans text-xs tracking-[0.24em] uppercase font-semibold mb-4" style={{ color: "#9a6bbd" }}>Our partners</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Building with our community.</h2>
            <p className="font-sans text-paper/60 mt-4 max-w-xl">The departments, organizations, and businesses moving with us.</p>
          </div>

          {partners.length === 0 ? (
            <p className="font-sans text-paper/50">Partners coming soon.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {partners.map((p) => {
                const inner = p.logo_url ? (
                  <img src={p.logo_url} alt={p.name} className="max-h-16 w-auto object-contain grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                ) : (
                  <span className="font-serif text-lg text-paper/80 group-hover:text-white text-center transition-colors">{p.name}</span>
                );
                return p.website_url ? (
                  <a
                    key={p.id}
                    href={p.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-${p.id}`}
                    className="group flex items-center justify-center text-center p-6 md:p-8 min-h-[120px] border border-paper/15 hover:border-paper/40 transition-colors"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={p.id} data-testid={`partner-${p.id}`} className="group flex items-center justify-center text-center p-6 md:p-8 min-h-[120px] border border-paper/15">
                    {inner}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-14">
            <Link
              to="/get-involved"
              data-testid="about-partner-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-paper text-ink font-sans font-medium hover:-translate-y-0.5 transition-all duration-300"
            >
              Become a Partner <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-sans text-xs tracking-[0.24em] uppercase text-ink/40 mb-1">Get in touch</p>
          <a href="mailto:hello@tigertrailride.org" className="font-serif text-2xl md:text-3xl font-bold text-ink hover:text-trailride transition-colors">
            hello@tigertrailride.org
          </a>
        </div>
        <Link to="/events" className="inline-flex items-center gap-1.5 font-sans font-medium text-trailride hover:gap-2.5 transition-all">
          Find an event <ArrowUpRight size={16} />
        </Link>
      </section>

      <Footer />
    </div>
  );
}
